import { AlbumContentType } from 'common/enums';
import { createUrlToDownloadFile } from 'helpers/file-utils';
import { EXT_MIME } from 'helpers/import-mime';
import { adaptContentType } from 'helpers/utils';
import { API_ROUTES } from 'networking/api-routes';
import { ApiService } from 'networking/api-service';
import { AlbumContentSerialized } from 'networking/types/album-detail';

// Uploads are named without their extension ("Deed", not "Deed.pdf"), so the
// saved file gets one back from its type.
const withExtension = (name: string, mime: string) => {
  if (/\.[a-z0-9]{1,8}$/i.test(name)) return name;
  const type = mime.split(';')[0].trim().toLowerCase();
  const known = Object.keys(EXT_MIME).find((ext) => EXT_MIME[ext] === type);
  const subtype = type.split('/')[1] ?? '';
  const extension = known ?? (/^[a-z0-9]{2,5}$/.test(subtype) ? subtype : '');
  return extension ? `${name}.${extension}` : name;
};

// Saves a memory to the user's computer: a note as a .docx, anything else as
// its file. Throws when there's nothing to download or the fetch fails.
export const downloadMemory = async (content: AlbumContentSerialized) => {
  let blob: Blob;
  let fileName: string;
  if (adaptContentType(content) === AlbumContentType.write) {
    const response = await ApiService.get<Blob>(
      API_ROUTES.DOWNLOAD_JOURNAL_ENTRY_DOCX(content.content.id),
      {},
      { responseType: 'blob' },
    );
    blob = response.data;
    fileName = `${content.name}.docx`;
  } else if (content.mediaUrl) {
    const response = await fetch(
      createUrlToDownloadFile(content.mediaUrl, content.name),
    );
    if (!response.ok) throw new Error(response.statusText);
    blob = await response.blob();
    fileName = withExtension(content.name, blob.type);
  } else {
    throw new Error('No file available for download');
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
